import { StyleSheet, Text as RNText, View } from 'react-native';

import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Text } from '@/components/Text';
import { colors, fonts, radii, spacing } from '@/constants/theme';
import type { SocialCreateCopy } from '@/types/socialCreate';

export type SocialCreateBannerProps = {
  copy: SocialCreateCopy;
  testID?: string;
};

/**
 * The Create tab's Deep Green banner (Figma "Create" frame): eyebrow,
 * headline, one line of body, over the page-16 "M" watermark. The headline
 * is the app's Heading, not the Figma's wide face: type follows the app
 * until the whole app changes. Every word is the server's
 * (`user.social_create_copy`): it is where pay gets promised.
 */
export function SocialCreateBanner({ copy, testID }: SocialCreateBannerProps) {
  return (
    <View style={styles.banner} testID={testID}>
      <RNText
        style={styles.watermark}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        M
      </RNText>
      {copy.eyebrow ? <Overline tone="accent">{copy.eyebrow}</Overline> : null}
      {copy.headline ? (
        <Heading size="3xl" tone="inverse">
          {copy.headline}
        </Heading>
      ) : null}
      {copy.body ? (
        <Text variant="caption" color="frost">
          {copy.body}
        </Text>
      ) : null}
    </View>
  );
}

const WATERMARK = 160;

const styles = StyleSheet.create({
  banner: {
    backgroundColor: colors.deepGreen,
    borderRadius: radii.lg,
    padding: spacing.lg,
    gap: spacing.sm,
    overflow: 'hidden',
  },
  watermark: {
    position: 'absolute',
    top: -24,
    right: -28,
    fontFamily: fonts.numeric,
    fontSize: WATERMARK,
    lineHeight: WATERMARK,
    color: colors.watermarkOnDark,
    transform: [{ rotate: '-8deg' }],
  },
});
